import * as THREE from 'three/webgpu';
import type { HeroModel } from '../../engine/character/HeroModel';
import type { PlayerController } from '../../engine/character/PlayerController';
import type { Engine } from '../../engine/core/Engine';
import { settings } from '../../engine/core/settings';
import type { GravityField } from '../../engine/gravity/GravityField';
import type { PhysicsWorld } from '../../engine/physics/PhysicsWorld';
import type { RenderOrigin } from '../../engine/render/RenderOrigin';
import { Attack, type Frame, type Motion } from './Attack';
import { CombatLog } from './CombatLog';
import { Enemies, type Enemy } from './Enemies';
import { type Reached, reach } from './reach';
import { CRIT_ARM, CRIT_RADIUS, resolveHit } from './resolve';
import { toScreen } from './screen';
import { WeaponView } from './WeaponView';
import { swingLookRate, WEAPONS, type Weapon } from './weapons';

const _q = new THREE.Quaternion();
const _c = new THREE.Vector3();
const _vel = new THREE.Vector3();
const _edge = new THREE.Vector3();
const _perp = new THREE.Vector3();
const _axisY = new THREE.Vector3(0, 1, 0);
const _axisX = new THREE.Vector3(1, 0, 0);
const _body = { center: new THREE.Vector3(), up: new THREE.Vector3(), radius: 0, height: 0 };
const _shakeQ = new THREE.Quaternion();
const _shakeE = new THREE.Euler();

/** A thrust that turned less than this (radians) stops at the first body it meets. */
const THRUST_SWEEP = 0.3;
/**
 * For HIT_SLOW_TIME s after each attack's hit point, the view turns at most HIT_SLOW × the
 * weapon's swing speed: the blow's weight, felt in the hand.
 */
const HIT_SLOW_TIME = 0.1;
const HIT_SLOW = 0.2;
/** Seconds the finesse circle shows after a release, and the swing's end (and the circle) after the hit. */
const RING_TIME = 1.5;
const END_TIME = 1;

/**
 * The combat prototype, for trying out how attacks feel (the user's design: attacks whose
 * power comes from how you move while making them: finesse, rotation, movement). Left mouse:
 * hold to wind up, release to strike. 1–5 pick a weapon, K puts the enemies back. It owns the
 * attack, the test enemies, the first-person weapon, a readout of the bonuses building up and
 * of the last hit, hit-stop, a little camera shake, and simple sounds.
 */
export class CombatTest {
  private weaponIndex = 0;
  private attack: Attack;
  readonly enemies: Enemies;
  private readonly frameIn: Frame = {
    feet: new THREE.Vector3(),
    eye: new THREE.Vector3(),
    forward: new THREE.Vector3(),
    up: new THREE.Vector3(),
    grounded: false,
  };
  private spawnedAt = -1;
  private clock = 0;
  private stopUntil = 0;
  /** Until when (performance.now, ms) the view turns slowly after a hit point. */
  private slowUntil = 0;
  private shake = 0;
  private readonly panel: HTMLDivElement;
  private lastHit = 'Hold the left mouse button to wind up, release to strike.';
  private readonly log = new CombatLog();
  private readonly sfx = new Sfx();
  private readonly view = new WeaponView(WEAPONS);
  private panelShown = '';
  /**
   * The finesse circle round the finesse angle, shown for a while after each release: dashed
   * until the aim has gone far enough out from it to count (CRIT_ARM), then solid; gold after a
   * critical hit.
   */
  private readonly ring: HTMLDivElement;
  private ringShown = 0;
  private ringArmed = false;
  private ringCrit = false;
  private ringLook = '';
  /** A red dot where the swing ended (the aim at the hit point), for END_TIME s: how close to the circle it came. */
  private readonly endDot: HTMLDivElement;
  private readonly endDir = new THREE.Vector3();
  private endShown = 0;

  constructor(
    private readonly engine: Engine,
    private readonly player: PlayerController,
    physics: PhysicsWorld,
    field: GravityField,
    origin: RenderOrigin,
    scene: THREE.Scene,
  ) {
    this.attack = new Attack(WEAPONS[0]);
    this.enemies = new Enemies(physics, field, origin, scene);
    scene.add(this.view.group);
    this.ring = document.createElement('div');
    this.ring.id = 'finesse-ring';
    this.ring.style.cssText =
      'position:fixed;border-radius:50%;box-sizing:border-box;transform:translate(-50%,-50%);pointer-events:none;z-index:7;display:none';
    document.body.appendChild(this.ring);
    this.endDot = document.createElement('div');
    this.endDot.id = 'swing-end';
    this.endDot.style.cssText =
      'position:fixed;width:8px;height:8px;margin:-4px 0 0 -4px;border-radius:50%;background:#ff3b30;box-shadow:0 0 0 1.5px rgba(255,255,255,.85),0 0 6px rgba(255,59,48,.8);pointer-events:none;z-index:8;display:none';
    document.body.appendChild(this.endDot);
    this.panel = document.createElement('div');
    this.panel.id = 'combat-panel';
    this.panel.style.cssText =
      'position:fixed;left:50%;bottom:18px;transform:translateX(-50%);min-width:420px;max-width:80vw;padding:8px 14px;background:rgba(10,12,16,.55);color:#e8e4da;font:13px/1.45 system-ui,sans-serif;border-radius:8px;pointer-events:none;z-index:6;white-space:pre-line;text-align:center';
    document.body.appendChild(this.panel);
  }

  /** The hero holds the weapon too, for the third-person view. */
  holdBy(hero: HeroModel): void {
    this.view.holdBy(hero);
  }

  get weapon(): Weapon {
    return this.attack.weapon;
  }

  /** Physics step (before the physics world steps). */
  fixedUpdate(dt: number): void {
    this.clock += dt;
    const p = this.player;
    const input = this.engine.input;
    const f = this.frameIn;
    p.cameraPose(1, f.eye, _q);
    f.forward.set(0, 0, -1).applyQuaternion(_q);
    p.feet(f.feet);
    f.up.copy(p.up);
    f.grounded = p.grounded;

    // Stand the enemies in front of the player once the ground there has collision.
    if (this.spawnedAt < 0 && this.clock > 0.5 && this.enemies.spawn(f.feet, f.up, f.forward)) {
      this.spawnedAt = this.clock;
    }

    if (p.controlsEnabled) {
      for (let i = 0; i < WEAPONS.length; i++) {
        if (input.wasPressed(`Digit${i + 1}`) && this.attack.phase === 'idle') {
          this.weaponIndex = i;
          this.attack = new Attack(WEAPONS[i]);
        }
      }
      if (input.wasPressed('KeyK')) this.enemies.reset();
      if (input.wasPressed('KeyJ')) this.log.toggle();
    }
    const held = p.controlsEnabled && input.isButtonDown(0);
    const wasWindup = this.attack.phase === 'windup';
    const motion = this.attack.update(dt, held, f);
    if (wasWindup && this.attack.phase === 'strike') {
      this.sfx.whoosh(0.4 + 0.6 * this.attack.charge);
      this.enemies.markRelease();
      // No circle when the aim is already on it (see Attack.circle).
      this.ringShown = this.attack.circle ? RING_TIME : 0;
      this.ringArmed = false;
      this.ringCrit = false;
    }
    if (this.attack.phase === 'strike') this.ringArmed = this.attack.feintReach >= CRIT_ARM;
    if (motion) this.land(motion);
    this.limitLook();
    this.enemies.update(dt, f);
  }

  /**
   * How fast the view may turn: while attacking (winding up, and from the release to the hit)
   * at most as fast as the weapon swings, and for a moment after the hit point much slower.
   * (Checked every step and every frame: during hit-stop the steps nearly pause.)
   */
  private limitLook(): void {
    const w = this.attack.weapon;
    const swinging = this.attack.phase === 'windup' || this.attack.phase === 'strike';
    this.player.lookRateLimit = swinging
      ? swingLookRate(w)
      : performance.now() < this.slowUntil
        ? HIT_SLOW * swingLookRate(w)
        : Number.POSITIVE_INFINITY;
  }

  /** The attack lands: find what it reaches, resolve and apply each hit. */
  private land(m: Motion): void {
    const w = this.attack.weapon;
    // Where the swing ended, shown for a moment, with the circle kept up as long.
    this.endDir.copy(m.forward);
    this.endShown = END_TIME;
    if (this.ringShown > 0) this.ringShown = Math.max(this.ringShown, END_TIME);
    // The hit point: the view drags for a moment, hit or miss.
    this.slowUntil = performance.now() + HIT_SLOW_TIME * 1000;
    const hits: { e: Enemy; at: THREE.Vector3; glancing: boolean }[] = [];
    for (const e of this.enemies.list) {
      if (!e.body || e.down > 0) continue;
      const r = this.reaches(w, m, e);
      if (r) hits.push({ e, at: r.at, glancing: r.glancing });
    }
    // A straight thrust stops at the first body it meets; a sweeping one carries on.
    if (w.kind === 'spear' && Math.abs(m.sweep) < THRUST_SWEEP && hits.length > 1) {
      hits.sort((x, y) => x.at.distanceTo(m.eye) - y.at.distanceTo(m.eye));
      hits.length = 1;
    }
    if (hits.length === 0) {
      this.lastHit = `${w.name}: missed`;
      this.log.add(`${w.name}: missed`, [], '#9a958c');
      return;
    }
    let most = 0;
    let blocked = false;
    let landed = false;
    const lines: string[] = [];
    for (const { e, at, glancing } of hits) {
      const r = resolveHit(w, m, this.enemies.target(e), glancing);
      if (r.blocked) blocked = true;
      else landed = true;
      if (r.crit > 0) this.ringCrit = true;
      const dmg = Math.round(r.damage);
      this.enemies.hit(e, r, at, `${dmg}\n${r.notes.join(' · ')}`);
      most = Math.max(most, r.damage);
      const knocked = r.push.length() >= 0.5 ? `, knocked ${r.push.length().toFixed(0)} m/s` : '';
      const beaten = e.hp <= 0 ? ', down!' : '';
      lines.push(`${e.kind.name}: ${dmg} damage (${r.notes.join(', ') || 'plain hit'}${knocked})${beaten}`);
    }
    this.lastHit = `${w.name}: ${Math.round(most)} damage${hits.length > 1 ? ` (${hits.length} hit)` : ''}`;
    this.log.add(w.name, lines, most >= 50 ? '#ffcf5a' : '#e8e4da');
    // Feel: a moment's pause on impact, a jolt of the view, and a thud.
    const pause = Math.min(0.14, 0.03 + most * 0.0012);
    this.stopUntil = performance.now() + pause * 1000;
    this.engine.timeScale = 0.05;
    this.shake = Math.min(1, most / 80);
    if (landed) this.sfx.thud(Math.min(1, 0.25 + most / 90));
    if (blocked) this.sfx.clang(landed ? 0.5 : 1);
  }

  /** Where and how squarely the attack meets enemy `e`, or null if it doesn't reach it. */
  private reaches(w: Weapon, m: Motion, e: Enemy): Reached | null {
    const body = _body;
    this.enemies.center(e, body.center);
    body.up.copy(e.up);
    body.radius = e.kind.radius;
    body.height = e.kind.height;
    return reach(w, m, body);
  }

  /** Per frame: hit-stop, the weapon in view, the enemies, and the readout (`warm`: booting, see WeaponView). */
  frame(
    dt: number,
    camera: THREE.Camera,
    viewQ: THREE.Quaternion,
    firstPerson: boolean,
    hudOn: boolean,
    warm = false,
  ): void {
    if (this.engine.timeScale < 1 && performance.now() >= this.stopUntil) this.engine.timeScale = 1;
    this.limitLook();
    this.view.frame(dt, this.attack, viewQ, firstPerson, this.player.pitch, warm);
    this.enemies.frame(dt, camera, hudOn);
    this.showRing(dt, camera, hudOn);
    this.showEnd(dt, camera, hudOn);
    this.panel.style.display = hudOn ? '' : 'none';
    this.log.setVisible(hudOn);
    if (hudOn) this.updatePanel();
  }

  /** The red dot where the last swing ended, for END_TIME s after its hit point. */
  private showEnd(dt: number, camera: THREE.Camera, hudOn: boolean): void {
    this.endShown = Math.max(0, this.endShown - dt);
    // Rendering is camera-relative: the eye is at the origin.
    if (this.endShown <= 0 || !hudOn || !toScreen(_edge.copy(this.endDir).multiplyScalar(10), camera, _c)) {
      this.endDot.style.display = 'none';
      return;
    }
    this.endDot.style.display = '';
    this.endDot.style.left = `${((_c.x + 1) / 2) * 100}%`;
    this.endDot.style.top = `${((1 - _c.y) / 2) * 100}%`;
    this.endDot.style.opacity = `${Math.min(1, this.endShown / 0.3)}`;
  }

  /** The finesse circle: round the finesse angle, CRIT_RADIUS across, for RING_TIME s after a release. */
  private showRing(dt: number, camera: THREE.Camera, hudOn: boolean): void {
    this.ringShown = Math.max(0, this.ringShown - dt);
    if (this.ringShown <= 0 || !hudOn) {
      this.ring.style.display = 'none';
      return;
    }
    // Rendering is camera-relative: the eye is at the origin. The circle's size on screen is the
    // distance to a direction CRIT_RADIUS off its centre.
    const dir = this.attack.finesseAngle;
    if (!toScreen(_edge.copy(dir).multiplyScalar(10), camera, _c)) {
      this.ring.style.display = 'none';
      return;
    }
    _perp.crossVectors(dir, Math.abs(dir.y) < 0.9 ? _axisY : _axisX).normalize();
    _edge
      .copy(dir)
      .multiplyScalar(Math.cos(CRIT_RADIUS))
      .addScaledVector(_perp, Math.sin(CRIT_RADIUS))
      .multiplyScalar(10)
      .project(camera);
    const w = window.innerWidth;
    const h = window.innerHeight;
    const radius = Math.max(4, Math.hypot(((_edge.x - _c.x) / 2) * w, ((_edge.y - _c.y) / 2) * h));
    this.ring.style.display = '';
    this.ring.style.left = `${((_c.x + 1) / 2) * 100}%`;
    this.ring.style.top = `${((1 - _c.y) / 2) * 100}%`;
    this.ring.style.width = this.ring.style.height = `${(2 * radius).toFixed(1)}px`;
    this.ring.style.opacity = `${Math.min(1, this.ringShown / 0.4)}`;
    const look = this.ringCrit ? 'crit' : this.ringArmed ? 'armed' : 'waiting';
    if (look !== this.ringLook) {
      this.ringLook = look;
      this.ring.style.border =
        look === 'crit'
          ? '3px solid #ffcc33'
          : look === 'armed'
            ? '2px solid #3d8bff'
            : '2px dashed rgba(61,139,255,.75)';
      this.ring.style.background =
        look === 'crit' ? 'rgba(255,204,51,.18)' : look === 'armed' ? 'rgba(61,139,255,.14)' : 'transparent';
      this.ring.style.boxShadow = look === 'waiting' ? 'none' : '0 0 8px rgba(61,139,255,.6)';
    }
  }

  /** Jolt the view after a heavy hit (call before the camera takes the view). */
  shakeView(viewQ: THREE.Quaternion, dt: number): void {
    if (this.shake <= 0.001) return;
    const s = this.shake * 0.035;
    _shakeE.set((Math.random() - 0.5) * s, (Math.random() - 0.5) * s, (Math.random() - 0.5) * s * 0.5);
    viewQ.multiply(_shakeQ.setFromEuler(_shakeE));
    this.shake *= Math.exp(-dt * 14);
  }

  private updatePanel(): void {
    const a = this.attack;
    const w = a.weapon;
    const names = WEAPONS.map((x, i) =>
      i === this.weaponIndex ? `[${i + 1} ${x.name}]` : `${i + 1} ${x.name}`,
    ).join('  ');
    const bar = (v: number) => `${'█'.repeat(Math.round(v * 12))}${'░'.repeat(12 - Math.round(v * 12))}`;
    let state = 'ready';
    if (a.phase === 'windup') state = `winding up ${bar(a.charge)} ${Math.round(a.charge * 100)}%`;
    else if (a.phase === 'strike') state = 'striking';
    else if (a.phase === 'recover') state = 'recovering';
    // The three bonuses building up in this attack, and the weapon's affinity for each.
    const aff = w.affinity;
    const arc = Math.round((a.arc * 180) / Math.PI);
    const tip = a.swingVelocity(_c).length();
    const moved = a.travel(_vel).length();
    const reach = Math.round((a.feintReach * 180) / Math.PI);
    const bonuses = `finesse: feint ${Math.round((a.feint * 180) / Math.PI)}°, ${reach}° out from the circle (×${aff.finesse}) · rotation: swing ${arc}° at ${tip.toFixed(0)} m/s (×${aff.rotation}) · moved ${moved.toFixed(1)} m (×${aff.movement})`;
    const hint =
      'After the release: Rotation: 2% per degree you swing · Movement: metres you and it close · Finesse: go out from the blue circle and end the strike in it for a critical (it pierces armor near it; the red dot shows where you ended) · K resets · J log';
    const text = `${names}\n${state}\n${bonuses}\n${hint}\n${this.lastHit}`;
    if (text !== this.panelShown) {
      this.panel.textContent = text;
      this.panelShown = text;
    }
  }
}

/** Tiny synthesized sounds: a whoosh on release, a thud on impact, a clang on a shield. */
class Sfx {
  private ctx: AudioContext | null = null;

  private get audio(): AudioContext | null {
    if (settings.muted) return null;
    if (!this.ctx && typeof AudioContext !== 'undefined') this.ctx = new AudioContext();
    return this.ctx;
  }

  whoosh(strength: number): void {
    const ctx = this.audio;
    if (!ctx) return;
    const t = ctx.currentTime;
    const len = 0.28;
    const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * len), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 1.2;
    bp.frequency.setValueAtTime(400, t);
    bp.frequency.exponentialRampToValueAtTime(2200, t + len);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.12 * strength, t + 0.06);
    g.gain.exponentialRampToValueAtTime(0.001, t + len);
    src.connect(bp).connect(g).connect(ctx.destination);
    src.start(t);
  }

  /** A blow on a shield: a short metallic ring (inharmonic partials) over a tick of noise. */
  clang(strength: number): void {
    const ctx = this.audio;
    if (!ctx) return;
    const t = ctx.currentTime;
    const out = ctx.createGain();
    out.gain.value = 0.22 * strength;
    out.connect(ctx.destination);
    for (const [freq, gain, decay] of [
      [620, 1, 0.45],
      [1480, 0.6, 0.3],
      [2350, 0.4, 0.2],
      [3310, 0.25, 0.12],
    ]) {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(freq, t);
      const g = ctx.createGain();
      g.gain.setValueAtTime(gain, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + decay);
      o.connect(g).connect(out);
      o.start(t);
      o.stop(t + decay + 0.05);
    }
    const len = 0.04;
    const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * len), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 2500;
    src.connect(hp).connect(out);
    src.start(t);
  }

  thud(strength: number): void {
    const ctx = this.audio;
    if (!ctx) return;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(140, t);
    o.frequency.exponentialRampToValueAtTime(45, t + 0.25);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.5 * strength, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
    o.connect(g).connect(ctx.destination);
    o.start(t);
    o.stop(t + 0.4);
  }
}
